import { useState } from "react";
import api from "@/lib/api";
import DateInput from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { NotePencil, Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const DIAS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const fmtFecha = (iso) => {
  const d = new Date(iso + "T12:00:00");
  return `${DIAS_ES[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/* Indicador chico para encabezados de tabla / celdas del calendario */
export function NotaBadge({ count, onClick, testId }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={`${count} nota${count > 1 ? "s" : ""} — ver abajo`}
      className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[9px] font-bold leading-none hover:bg-sky-200 transition-colors"
      data-testid={testId}
    >
      <NotePencil size={10} weight="fill" /> {count}
    </button>
  );
}

/* Diálogo alta / edición */
export function NotaDialog({ open, onClose, nota, fechaDefault, onSaved }) {
  const [fecha, setFecha] = useState(nota?.fecha || fechaDefault || "");
  const [texto, setTexto] = useState(nota?.texto || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!fecha) return toast.error("Elegí la fecha");
    if (!texto.trim()) return toast.error("Escribí la nota");
    setSaving(true);
    try {
      const body = { fecha, texto: texto.trim() };
      const { data } = nota?.id
        ? await api.put(`/cashflow-2/notas/${nota.id}`, body)
        : await api.post("/cashflow-2/notas", body);
      toast.success(nota?.id ? "Nota actualizada" : "Nota agregada");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cf2-nota-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <NotePencil size={18} weight="duotone" className="text-sky-700" />
            {nota?.id ? "Editar nota" : "Nueva nota"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Día</Label>
            <DateInput value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-9 w-44" data-testid="cf2-nota-fecha" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Nota</Label>
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Ej: Confirmar transferencia de Pérez · Pagar alquiler cámara"
              autoFocus
              data-testid="cf2-nota-texto"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cf2-nota-cancelar">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="cf2-nota-guardar">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Panel de notas del período (debajo de la tabla / calendario) */
export function NotasPanel({ notas, periodoLabel, onNueva, onEditar, onBorrar, hoy }) {
  const porDia = notas.reduce((acc, n) => { (acc[n.fecha] ||= []).push(n); return acc; }, {});
  const fechas = Object.keys(porDia).sort();

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="cf2-notas-panel">
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-sky-50/70">
        <div className="flex items-center gap-2">
          <NotePencil size={18} weight="duotone" className="text-sky-700" />
          <span className="font-heading font-bold text-[14px] text-slate-800">Notas</span>
          <span className="text-[11px] font-mono text-slate-500">{periodoLabel}</span>
          {notas.length > 0 && <span className="text-[10px] font-mono bg-sky-100 text-sky-800 rounded-full px-2 py-0.5">{notas.length}</span>}
        </div>
        <Button size="sm" variant="outline" onClick={() => onNueva()} className="h-7 gap-1 text-[12px]" data-testid="cf2-nota-nueva">
          <Plus size={13} weight="bold" /> Nueva nota
        </Button>
      </div>
      {fechas.length === 0 ? (
        <div className="px-4 py-5 text-[12px] text-slate-400 italic text-center">
          Sin notas en este período. Hacé clic en un día o en “Nueva nota” para agregar una.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {fechas.map((f) => (
            <div key={f} className="flex gap-4 px-4 py-2.5" data-testid={`cf2-notas-dia-${f}`}>
              <div className="w-44 shrink-0 pt-0.5">
                <div className={`text-[12px] font-semibold ${f === hoy ? "text-amber-800" : "text-slate-700"}`}>{fmtFecha(f)}</div>
                {f === hoy && <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold">Hoy</div>}
                <button
                  type="button"
                  onClick={() => onNueva(f)}
                  className="mt-1 text-[11px] text-sky-700 hover:underline inline-flex items-center gap-0.5"
                  data-testid={`cf2-nota-agregar-${f}`}
                >
                  <Plus size={10} weight="bold" /> agregar
                </button>
              </div>
              <ul className="flex-1 space-y-1.5">
                {porDia[f].map((n) => (
                  <li key={n.id} className="group flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50" data-testid={`cf2-nota-${n.id}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">{n.texto}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{n.created_by}</div>
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => onEditar(n)} className="p-1 rounded hover:bg-slate-200 text-slate-600" title="Editar" data-testid={`cf2-nota-editar-${n.id}`}>
                        <PencilSimple size={13} />
                      </button>
                      <button type="button" onClick={() => onBorrar(n)} className="p-1 rounded hover:bg-rose-100 text-rose-600" title="Eliminar" data-testid={`cf2-nota-borrar-${n.id}`}>
                        <Trash size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
